// PUBLIC — create a PayFast-bound order for an automated document service.
// Pricing is resolved server-side from the canonical catalog; the client
// never supplies an amount. The resulting reference is what
// `createPayfastCheckout` and the PayFast ITN webhook recognise.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { DOCUMENT_SERVICE_MAP } from "@/lib/document-services";

const AnswerSchema = z.record(z.string(), z.string().max(4000));

function makeRef(id: string) {
  const prefix = id.replace(/[^a-z0-9]/gi, "").slice(0, 3).toUpperCase().padEnd(3, "X");
  const stamp = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `NDL-DOC${prefix}-${stamp}-${rand}`;
}

export const createDocumentServiceOrder = createServerFn({ method: "POST" })
  .inputValidator((i: unknown) =>
    z
      .object({
        serviceId: z.string().min(1).max(80),
        clientName: z.string().trim().min(2).max(200),
        clientEmail: z.string().trim().email().max(200),
        clientPhone: z.string().trim().max(60).optional().default(""),
        answers: AnswerSchema.default({}),
      })
      .parse(i),
  )
  .handler(async ({ data }) => {
    const service = DOCUMENT_SERVICE_MAP[data.serviceId];
    if (!service) throw new Error("Unknown document service");

    const { assertRateLimit } = await import("@/lib/api/_rate-limit.server");
    await assertRateLimit({
      scope: "document-service-order",
      key: data.clientEmail.toLowerCase(),
      windowMinutes: 10,
      limit: 8,
    });

    // Only keep answers that belong to this service's declared fields.
    const allowed = new Set(service.fields.map((f) => f.name));
    const answers: Record<string, string> = {};
    for (const [k, v] of Object.entries(data.answers)) {
      if (allowed.has(k) && v.trim()) answers[k] = v.trim();
    }
    for (const f of service.fields) {
      if (f.required && !answers[f.name]) {
        throw new Error(`Please complete: ${f.label}`);
      }
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { generateUnlockPassword } = await import("@/lib/pdf-protect.server");

    const reference = makeRef(service.id);
    const { data: row, error } = await supabaseAdmin
      .from("contract_orders")
      .insert({
        slug: `doc-service:${service.id}`,
        title: service.name,
        client_name: data.clientName,
        client_email: data.clientEmail,
        client_phone: data.clientPhone || null,
        vars: {
          document_service: {
            id: service.id,
            name: service.name,
            turnaround: service.turnaround,
            answers,
          },
        } as any,
        amount_zar: service.priceZar,
        reference,
        status: "pending",
        unlock_password: generateUnlockPassword(),
      })
      .select("reference, amount_zar")
      .single();
    if (error) throw new Error(error.message);

    return {
      reference: row.reference as string,
      amountZar: Number(row.amount_zar),
      serviceName: service.name,
    };
  });
